import axios from 'axios';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://192.99.55.137:8000',
  timeout: 30000, // 30 second timeout for enterprise-level reliability
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('piscicare_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // Handle file uploads - don't set Content-Type for FormData
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors and provide better error messages
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Enhanced error handling for enterprise-level application
    if (error.response?.status === 401) {
      localStorage.removeItem('piscicare_token');
      localStorage.removeItem('piscicare_user');
      window.location.href = '/login';
    } else if (error.code === 'ECONNABORTED') {
      // Handle timeout errors
      error.message = 'Request timeout. Please check your connection and try again.';
    } else if (error.response?.status >= 500) {
      // Handle server errors
      error.message = 'Server error. Please try again later or contact support.';
    } else if (error.response?.status === 404) {
      // Handle not found errors
      error.message = 'Requested resource not found.';
    } else if (!error.response) {
      // Handle network errors
      error.message = 'Network error. Please check your connection.';
    }
    
    // Log error for debugging in development
    if (process.env.NODE_ENV === 'development') {
      console.error('API Error:', {
        status: error.response?.status,
        message: error.message,
        url: error.config?.url,
        method: error.config?.method
      });
    }
    
    return Promise.reject(error);
  }
);

// Authentication API
export const authAPI = {
  login: (email, password) => {
    return api.post('/auth/login', {
      email: email,
      password: password
    });
  },
  
  register: (userData) => api.post('/auth/register', userData),
};

// Farms API
export const farmsAPI = {
  getAll: () => api.get('/api/v1/farms'),
  getById: (id) => api.get(`/api/v1/farms/${id}`),
  create: (farmData) => api.post('/api/v1/farms', farmData),
  update: (id, farmData) => api.put(`/api/v1/farms/${id}`, farmData),
  delete: (id) => api.delete(`/api/v1/farms/${id}`),
};

// Ponds API
export const pondsAPI = {
  getByFarm: (farmId) => api.get(`/api/v1/farms/${farmId}/ponds`),
  getById: (id) => api.get(`/api/v1/ponds/${id}`),
  create: (pondData) => api.post('/api/v1/ponds', pondData),
  update: (id, pondData) => api.put(`/api/v1/ponds/${id}`, pondData),
  delete: (id) => api.delete(`/api/v1/ponds/${id}`),
  // Helper function to get all ponds for a user by getting all their farms first
  getAllForUser: async () => {
    try {
      const farmsResponse = await api.get('/api/v1/farms');
      const farms = farmsResponse.data;
      const pondPromises = farms.map(async (farm) => {
        try {
          const response = await api.get(`/api/v1/farms/${farm.id}/ponds`);
          return response.data;
        } catch (error) {
          console.warn(`Failed to load ponds for farm ${farm.id}:`, error);
          return [];
        }
      });
      const pondResponses = await Promise.all(pondPromises);
      return pondResponses.flatMap(ponds => ponds);
    } catch (error) {
      console.error('Failed to load farms or ponds:', error);
      return [];
    }
  },
};

// Policies API
export const policiesAPI = {
  getAll: () => api.get('/api/v1/policies'),
  getById: (id) => api.get(`/api/v1/policies/${id}`),
  create: (policyData) => api.post('/api/v1/policies', policyData),
  update: (id, policyData) => api.put(`/api/v1/policies/${id}`, policyData),
  delete: (id) => api.delete(`/api/v1/policies/${id}`),
  calculatePremium: (calculationData) => api.post('/api/v1/policies/calculate', calculationData),
  approve: (id, policyUpdate) => api.put(`/api/v1/policies/${id}/approve`, policyUpdate),
  updateStatus: (id, status, approvedBy, notes) => 
    api.put(`/api/v1/policies/${id}/status?status=${status}&approved_by=${approvedBy || ''}&notes=${notes || ''}`),
  renew: (id, newDocRequested) => api.post(`/api/v1/policies/${id}/renew?new_doc_requested=${newDocRequested}`),
};

// Claims API
export const claimsAPI = {
  getAll: () => api.get('/api/v1/claims/'),
  getById: (id) => api.get(`/api/v1/claims/${id}`),
  create: (claimData) => api.post('/api/v1/claims/', claimData),
  update: (id, claimData) => api.put(`/api/v1/claims/${id}`, claimData),
  delete: (id) => api.delete(`/api/v1/claims/${id}`),
  process: (id, claimUpdate) => api.put(`/api/v1/claims/${id}/process`, claimUpdate),
  getByPolicy: (policyId) => api.get(`/api/v1/claims/policy/${policyId}`),
  getByUser: (userId) => api.get(`/api/v1/claims/user/${userId}`),
};

// Documents API
export const documentsAPI = {
  getByPolicy: (policyId) => api.get(`/api/v1/documents/policy/${policyId}`),
  getMyDocuments: () => api.get('/api/v1/documents/my-documents'),
  upload: (documentData) => api.post('/api/v1/documents/upload', documentData),
  uploadGeospatial: (documentData) => api.post('/api/v1/documents/upload-geospatial', documentData),
  verify: (id, verified, comments) => 
    api.put(`/api/v1/documents/${id}/verify?verified=${verified}&comments=${comments || ''}`),
  // Helper to get all documents for user's policies
  getAllForUser: async () => {
    const policies = await api.get('/api/v1/policies');
    const docPromises = policies.data.map(policy => 
      api.get(`/api/v1/documents/policy/${policy.id}`)
    );
    const docResponses = await Promise.all(docPromises);
    return docResponses.flatMap(response => response.data);
  },
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/api/v1/dashboard/stats'),
  getFarmerDashboard: () => api.get(`/api/v1/dashboard/farmer`),
  getAdminDashboard: () => api.get('/api/v1/dashboard/admin'),
  getAgentDashboard: (userId) => api.get(`/api/v1/dashboard/agent/${userId}`),
  getPolicyAnalytics: () => api.get('/api/v1/dashboard/analytics/policies'),
  getSpeciesAnalytics: () => api.get('/api/v1/dashboard/analytics/species'),
  getGeographicAnalytics: () => api.get('/api/v1/dashboard/analytics/geographic'),
  // Optimized bulk data endpoint for admin dashboard
  getAdminBulkData: () => api.get('/api/v1/admin/bulk-data'),
  
  // Optimized bulk data endpoint for farmer dashboard
  getFarmerBulkData: () => api.get('/api/v1/farmer/bulk-data'),
};

// User Management API (Admin only)
export const usersAPI = {
  getAll: (includeDeleted = false) =>
    api.get('/api/v1/users', { params: includeDeleted ? { include_deleted: true } : {} }),
  getById: (id) => api.get(`/api/v1/users/${id}`),
  update: (id, userData) => api.put(`/api/v1/users/${id}`, userData),
  updateStatus: (id, isActive) => api.put(`/api/v1/users/${id}/status`, { is_active: isActive }),
  delete: (id) => api.delete(`/api/v1/users/${id}`),
  restore: (id) => api.post(`/api/v1/admin/users/${id}/restore`),
  purge: (id, reason) => api.delete(`/api/v1/admin/users/${id}/purge`, { params: { reason } }),
  resetPassword: (id) => api.post(`/api/v1/admin/users/${id}/reset-password`),
  completeFirstLogin: () => api.patch('/api/v1/users/me/complete-first-login'),
};

// Lifecycle API
export const lifecycleAPI = {
  getStatus: () => api.get('/api/v1/lifecycle/status'),
};

// RBAC APIs
export const rbacAPI = {
  getMyRoles: () => api.get('/api/v1/rbac/my-roles'),
  listRoles: () => api.get('/api/v1/rbac/roles'),
  getRole: (roleName) => api.get(`/api/v1/rbac/roles/${roleName}`),
  listPermissions: (module) => api.get('/api/v1/rbac/permissions', { params: module ? { module } : {} }),
  assignRole: (userId, roleName) => api.post('/api/v1/rbac/users/assign-role', { user_id: userId, role_name: roleName }),
  revokeRole: (userId, roleName) => api.post('/api/v1/rbac/users/revoke-role', { user_id: userId, role_name: roleName }),
  getUserRoles: (userId) => api.get(`/api/v1/rbac/users/${userId}/roles`),
  seedData: () => api.post('/api/v1/rbac/seed'),
};

// Request Management APIs
export const requestsAPI = {
  // Farmer endpoints
  create: (requestData) => api.post('/api/v1/requests', requestData),
  getAll: () => api.get('/api/v1/requests'),
  getById: (id) => api.get(`/api/v1/requests/${id}`),
  update: (id, requestData) => api.put(`/api/v1/requests/${id}`, requestData),
  
  // Admin endpoints
  getAllAdmin: (statusFilter, requestType) => {
    const params = new URLSearchParams();
    if (statusFilter) params.append('status_filter', statusFilter);
    if (requestType) params.append('request_type', requestType);
    return api.get(`/api/v1/admin/requests?${params.toString()}`);
  },
  approve: (id) => api.put(`/api/v1/admin/requests/${id}/approve`),
  reject: (id, rejectionReason) => api.put(`/api/v1/admin/requests/${id}/reject?rejection_reason=${encodeURIComponent(rejectionReason)}`),
};

// Stocking Events API
export const stockingEventsAPI = {
  getAll: () => api.get('/api/v1/stocking-events'),
  getById: (id) => api.get(`/api/v1/stocking-events/${id}`),
  getByPond: (pondId) => api.get(`/api/v1/ponds/${pondId}/stocking-events`),
  create: (eventData) => api.post('/api/v1/stocking-events', eventData),
  update: (id, eventData) => api.put(`/api/v1/stocking-events/${id}`, eventData),
  delete: (id) => api.delete(`/api/v1/stocking-events/${id}`),
};

// Water Reservoirs API
export const waterReservoirsAPI = {
  getByFarm: (farmId) => api.get(`/api/v1/farms/${farmId}/water-reservoirs`),
  create: (farmId, reservoirData) => api.post(`/api/v1/farms/${farmId}/water-reservoirs`, reservoirData),
  update: (farmId, reservoirId, reservoirData) => api.put(`/api/v1/farms/${farmId}/water-reservoirs/${reservoirId}`, reservoirData),
  delete: (farmId, reservoirId) => api.delete(`/api/v1/farms/${farmId}/water-reservoirs/${reservoirId}`),
};

// Underwriting Decisions API
export const underwritingDecisionsAPI = {
  getByPolicy: (policyId) => api.get(`/api/v1/policies/${policyId}/underwriting-decision`),
  create: (policyId, decisionData) => api.post(`/api/v1/policies/${policyId}/underwriting-decision`, decisionData),
  update: (policyId, decisionId, decisionData) => api.put(`/api/v1/policies/${policyId}/underwriting-decision`, decisionData),
};

// Survey Reports API
export const surveyReportsAPI = {
  getByClaim: (claimId) => api.get(`/api/v1/claims/${claimId}/survey-report`),
  create: (claimId, reportData) => api.post(`/api/v1/claims/${claimId}/survey-report`, reportData),
  update: (claimId, reportId, reportData) => api.put(`/api/v1/claims/${claimId}/survey-report`, reportData),
};

// Lab Reports API
export const labReportsAPI = {
  // Get lab reports for a pond
  getByPond: (pondId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.testType) queryParams.append('test_type', params.testType);
    if (params.resultStatus) queryParams.append('result_status', params.resultStatus);
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/api/v1/ponds/${pondId}/lab-reports${queryString ? '?' + queryString : ''}`);
  },
  
  // Get single lab report by ID
  getById: (reportId) => api.get(`/api/v1/lab-reports/${reportId}`),
  
  // Get lab report summary for a pond
  getSummary: (pondId) => api.get(`/api/v1/ponds/${pondId}/lab-reports/summary`),
  
  // Get recent lab reports for all ponds (farmer dashboard)
  getRecent: (limit = 10) => api.get(`/api/v1/lab-reports/recent?limit=${limit}`),
  
  // Admin/Lab endpoints (for future use)
  admin: {
    create: (reportData) => api.post('/api/v1/lab-reports', reportData),
    update: (reportId, reportData) => api.put(`/api/v1/lab-reports/${reportId}`, reportData),
    uploadAttachment: (reportId, file) => {
      const formData = new FormData();
      formData.append('file', file);
      return api.post(`/api/v1/lab-reports/${reportId}/attachments`, formData);
    },
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.farmerId) queryParams.append('farmer_id', params.farmerId);
      if (params.pondId) queryParams.append('pond_id', params.pondId);
      if (params.testType) queryParams.append('test_type', params.testType);
      if (params.resultStatus) queryParams.append('result_status', params.resultStatus);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      const queryString = queryParams.toString();
      return api.get(`/api/v1/lab-reports/admin/all${queryString ? '?' + queryString : ''}`);
    },
  }
};

// KYC API
export const kycAPI = {
  // Farmer endpoints
  getStatus: () => api.get('/api/v1/kyc/status'),
  submit: (kycData) => api.post('/api/v1/kyc/submit', kycData),
  getMySubmissions: () => api.get('/api/v1/kyc/my-submissions'),
  
  // Admin endpoints
  getPending: (status) => api.get(`/api/v1/kyc/admin/pending${status ? `?status=${status}` : ''}`),
  getDetails: (kycId) => api.get(`/api/v1/kyc/admin/${kycId}`),
  startReview: (kycId) => api.put(`/api/v1/kyc/admin/${kycId}/start-review`),
  approve: (kycId, data) => api.put(`/api/v1/kyc/admin/${kycId}/approve`, data || {}),
  reject: (kycId, data) => api.put(`/api/v1/kyc/admin/${kycId}/reject`, data),
  getStats: () => api.get('/api/v1/kyc/admin/stats'),
};

// Feed Events API
export const feedEventsAPI = {
  // Get feed events for a pond
  getByPond: (pondId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.startDate) queryParams.append('start_date', params.startDate);
    if (params.endDate) queryParams.append('end_date', params.endDate);
    if (params.feedType) queryParams.append('feed_type', params.feedType);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/api/v1/ponds/${pondId}/feed-events${queryString ? '?' + queryString : ''}`);
  },
  
  // Create new feed event
  create: (pondId, feedData) => api.post(`/api/v1/ponds/${pondId}/feed-events`, feedData),
  
  // Get feed event by ID
  getById: (eventId) => api.get(`/api/v1/feed-events/${eventId}`),
  
  // Get feed summary for a pond
  getSummary: (pondId) => api.get(`/api/v1/ponds/${pondId}/feed-summary`),
  
  // Get feed calendar for a month
  getCalendar: (pondId, month, year) => api.get(`/api/v1/ponds/${pondId}/feed-calendar?month=${month}&year=${year}`),
  
  // Get feed timeline
  getTimeline: (pondId, days = 7) => api.get(`/api/v1/ponds/${pondId}/feed-timeline?days=${days}`),
};

// Alerts API
export const alertsAPI = {
  // Get alert counts for badge
  getCount: () => api.get('/api/v1/alerts/count'),
  
  // Get alerts with optional filters
  getAll: (params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.pondId) queryParams.append('pond_id', params.pondId);
    if (params.severity) queryParams.append('severity', params.severity);
    if (params.acknowledged !== undefined) queryParams.append('acknowledged', params.acknowledged);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/api/v1/alerts/${queryString ? '?' + queryString : ''}`);
  },
  
  // Get unacknowledged alerts for dropdown
  getUnacknowledged: (limit = 10) => api.get(`/api/v1/alerts/unacknowledged?limit=${limit}`),
  
  // Get single alert
  getById: (alertId) => api.get(`/api/v1/alerts/${alertId}`),
  
  // Acknowledge alert
  acknowledge: (alertId) => api.put(`/api/v1/alerts/${alertId}/acknowledge`),
  
  // Acknowledge all alerts
  acknowledgeAll: () => api.put('/api/v1/alerts/acknowledge-all'),
  
  // Admin endpoints
  admin: {
    create: (alertData) => api.post('/api/v1/alerts/admin/create', alertData),
    getAll: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.alertType) queryParams.append('alert_type', params.alertType);
      if (params.severity) queryParams.append('severity', params.severity);
      if (params.source) queryParams.append('source', params.source);
      if (params.farmerId) queryParams.append('farmer_id', params.farmerId);
      if (params.pondId) queryParams.append('pond_id', params.pondId);
      if (params.acknowledged !== undefined) queryParams.append('acknowledged', params.acknowledged);
      if (params.isCurated !== undefined) queryParams.append('is_curated', params.isCurated);
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.offset) queryParams.append('offset', params.offset);
      const queryString = queryParams.toString();
      return api.get(`/api/v1/alerts/admin/all${queryString ? '?' + queryString : ''}`);
    },
    getStats: () => api.get('/api/v1/alerts/admin/stats'),
  }
};

// Weather API
export const weatherAPI = {
  // Get weather for widget (minimal data)
  getWidget: () => api.get('/api/v1/weather/widget'),
  
  // Get full weather for a farm
  getFarmWeather: (farmId, forceRefresh = false) => 
    api.get(`/api/v1/weather/farm/${farmId}${forceRefresh ? '?force_refresh=true' : ''}`),
  
  // Get weather for a pond (uses farm coordinates)
  getPondWeather: (pondId) => api.get(`/api/v1/weather/pond/${pondId}`),
  
  // Get weather for all user's farms
  getAllFarmsWeather: () => api.get('/api/v1/weather/all-farms'),
  
  // Admin endpoints
  admin: {
    setApiKey: (apiKey) => api.post('/api/v1/weather/admin/api-key', { api_key: apiKey }),
    getApiKeyStatus: () => api.get('/api/v1/weather/admin/api-key/status'),
    getFeatureStatus: () => api.get('/api/v1/weather/admin/feature-status'),
    toggleFeature: (enabled) => api.post(`/api/v1/weather/admin/toggle?enabled=${enabled}`),
    testConnection: (lat, lon) => api.post(`/api/v1/weather/admin/test?lat=${lat}&lon=${lon}`),
    getCacheStats: () => api.get('/api/v1/weather/admin/cache-stats'),
    clearCache: (farmId) => api.delete(`/api/v1/weather/admin/cache/clear${farmId ? `?farm_id=${farmId}` : ''}`),
  }
};

// Timeline API - Unified Pond Timeline
export const timelineAPI = {
  // Get timeline for a pond
  getPondTimeline: (pondId, params = {}) => {
    const queryParams = new URLSearchParams();
    if (params.eventType) queryParams.append('event_type', params.eventType);
    if (params.significantOnly) queryParams.append('significant_only', params.significantOnly);
    if (params.claimEvidenceOnly) queryParams.append('claim_evidence_only', params.claimEvidenceOnly);
    if (params.limit) queryParams.append('limit', params.limit);
    if (params.offset) queryParams.append('offset', params.offset);
    const queryString = queryParams.toString();
    return api.get(`/api/v1/timeline/pond/${pondId}${queryString ? '?' + queryString : ''}`);
  },
  
  // Get timeline stats for a pond
  getPondTimelineStats: (pondId) => api.get(`/api/v1/timeline/pond/${pondId}/stats`),
  
  // Get claim evidence timeline
  getClaimEvidence: (pondId) => api.get(`/api/v1/timeline/pond/${pondId}/claim-evidence`),
  
  // Create a note in the timeline
  createNote: (noteData) => api.post('/api/v1/timeline/note', noteData),
  
  // Get event types
  getEventTypes: () => api.get('/api/v1/timeline/event-types'),
  
  // Admin endpoints
  admin: {
    getRecentEvents: (params = {}) => {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.eventType) queryParams.append('event_type', params.eventType);
      const queryString = queryParams.toString();
      return api.get(`/api/v1/timeline/admin/recent${queryString ? '?' + queryString : ''}`);
    }
  }
};

// Blocking Rules API - Check eligibility before actions
export const blockingAPI = {
  // Check if policy creation is allowed for a pond
  checkPolicyCreation: (pondId) => api.get(`/api/v1/blocking/policy-creation/${pondId}`),
  
  // Check if claim submission is allowed for a policy
  checkClaimSubmission: (policyId, lossPercentage = 0) => {
    const params = lossPercentage > 0 ? `?loss_percentage=${lossPercentage}` : '';
    return api.get(`/api/v1/blocking/claim-submission/${policyId}${params}`);
  },
  
  // Check if claim payment is allowed
  checkClaimPayment: (claimId) => api.get(`/api/v1/blocking/claim-payment/${claimId}`),
  
  // Get comprehensive farmer blocking status
  getFarmerStatus: () => api.get('/api/v1/blocking/farmer-status'),
  
  // Get policy eligibility for all ponds
  getAllPondsEligibility: () => api.get('/api/v1/blocking/all-ponds'),
  
  // Get claim eligibility for all policies
  getAllPoliciesClaimEligibility: () => api.get('/api/v1/blocking/all-policies'),
};

// Credit API
export const creditAPI = {
  getApplications: () => api.get('/api/v1/credit/applications'),
  getApplication: (id) => api.get(`/api/v1/credit/applications/${id}`),
  createApplication: (data) => api.post('/api/v1/credit/applications', data),
  getLimits: () => api.get('/api/v1/credit/limits'),
  getHistory: () => api.get('/api/v1/credit/history'),
  getRepaymentSchedule: (applicationId) => api.get(`/api/v1/credit/repayment-schedule?application_id=${applicationId}`),
  getOutstanding: (applicationId) => api.get(`/api/v1/credit/applications/${applicationId}/outstanding`),
  createRepayment: (data) => api.post('/api/v1/credit/repayments', data),
};

// Admin Credit API (enriched views + approve/reject)
export const adminCreditAPI = {
  getApplications: (status) =>
    api.get('/api/v1/admin/credit/applications', { params: status ? { status } : {} }),
  approve: (id, data) => api.post(`/api/v1/admin/credit/${id}/approve`, data),
  reject: (id, data) => api.post(`/api/v1/admin/credit/${id}/reject`, data),
};

// Escrow API
// Legacy escrow deprecated. Ledger is single source of truth.
// Legacy endpoints (getAccounts, createAccount, getBalance, getTransactions)
// have been removed. All escrow operations now use double-entry ledger only.
export const escrowAPI = {
  // Double-entry ledger endpoints (single source of truth)
  depositCredit: (data) => api.post('/api/v1/escrow/ledger/credit-in', data),
  vendorPayment: (data) => api.post('/api/v1/escrow/ledger/vendor-payment', data),
  depositSettlement: (data) => api.post('/api/v1/escrow/ledger/settlement-in', data),
  executeSettlement: (data) => api.post('/api/v1/escrow/ledger/settle', data),
  getLedgerEntries: (creditCaseId) => api.get(`/api/v1/escrow/ledger/${creditCaseId}/entries`),
  getLedgerBalance: (creditCaseId) => api.get(`/api/v1/escrow/ledger/${creditCaseId}/balance`),
};

// Marketplace API
export const marketplaceAPI = {
  getVendors: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.category) qp.append('category', params.category);
    if (params.status) qp.append('status', params.status);
    if (params.city) qp.append('city', params.city);
    if (params.search) qp.append('search', params.search);
    if (params.verified !== undefined) qp.append('verified', params.verified);
    const qs = qp.toString();
    return api.get(`/api/v1/marketplace/vendors${qs ? '?' + qs : ''}`);
  },
  getVendor: (id) => api.get(`/api/v1/marketplace/vendors/${id}`),
  createVendor: (data) => api.post('/api/v1/marketplace/vendors', data),
  updateVendorStatus: (vendorId, status) =>
    api.put(`/api/v1/marketplace/vendors/${vendorId}/status`, { status }),
  updateVendorBank: (vendorId, data) =>
    api.put(`/api/v1/marketplace/vendors/${vendorId}/bank`, data),
  verifyVendor: (vendorId, verified) =>
    api.put(`/api/v1/marketplace/vendors/${vendorId}/verify`, { verified }),
  getVendorLedger: (vendorId) => api.get(`/api/v1/marketplace/vendors/${vendorId}/ledger`),
  getVendorPayments: (vendorId) => api.get(`/api/v1/marketplace/vendors/${vendorId}/payments`),
  getProducts: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.categoryId) qp.append('category_id', params.categoryId);
    if (params.vendorId) qp.append('vendor_id', params.vendorId);
    const qs = qp.toString();
    return api.get(`/api/v1/marketplace/products${qs ? '?' + qs : ''}`);
  },
  getProduct: (id) => api.get(`/api/v1/marketplace/products/${id}`),
  createOrder: (data) => api.post('/api/v1/marketplace/orders', data),
  getOrders: () => api.get('/api/v1/marketplace/orders'),
  getOrder: (id) => api.get(`/api/v1/marketplace/orders/${id}`),
  createRating: (data) => api.post('/api/v1/marketplace/ratings', data),
};

// Harvests API
export const harvestsAPI = {
  getAll: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.pondId) qp.append('pond_id', params.pondId);
    if (params.status) qp.append('status', params.status);
    const qs = qp.toString();
    return api.get(`/api/v1/harvests${qs ? '?' + qs : ''}`);
  },
  getById: (id) => api.get(`/api/v1/harvests/${id}`),
  create: (data) => api.post('/api/v1/harvests', data),
};

// Settlement API (part of the credit pipeline)
export const settlementAPI = {
  getAll: () => api.get('/api/v1/settlements'),
  getById: (id) => api.get(`/api/v1/settlements/${id}`),
};

// Vendor API
export const vendorAPI = {
  getDashboard: () => api.get('/api/v1/vendor/dashboard'),
  getOrders: (statusFilter) =>
    api.get(`/api/v1/vendor/orders${statusFilter ? `?status_filter=${statusFilter}` : ''}`),
  uploadInvoice: (orderId, invoiceNumber, file) => {
    const formData = new FormData();
    formData.append('invoice_number', invoiceNumber);
    formData.append('invoice_file', file);
    return api.post(
      `/api/v1/vendor/orders/${orderId}/invoice`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  },
  getPayments: () => api.get('/api/v1/vendor/payments'),
  getProfile: () => api.get('/api/v1/vendor/profile'),
  updateProfile: (data) => api.put('/api/v1/vendor/profile', data),
};

// Payment Gateway Admin API
export const paymentGatewayAPI = {
  // Get overall gateway status (enabled, keys configured, mode)
  getStatus: () => api.get('/api/v1/admin/payment/status'),

  // List all payment settings (masked)
  getSettings: () => api.get('/api/v1/admin/payment/settings'),

  // Update a single payment setting
  updateSetting: (key, value) =>
    api.put('/api/v1/admin/payment/settings', { key, value }),

  // Enable/disable the payment gateway
  toggle: (enabled) =>
    api.post('/api/v1/admin/payment/toggle', { enabled }),

  // Test Razorpay API connection
  testConnection: () => api.post('/api/v1/admin/payment/test'),

  // Transaction monitoring
  getTransactions: (params = {}) => {
    const qp = new URLSearchParams();
    if (params.limit) qp.append('limit', params.limit);
    if (params.offset) qp.append('offset', params.offset);
    if (params.status) qp.append('status_filter', params.status);
    const qs = qp.toString();
    return api.get(`/api/v1/admin/payment/transactions${qs ? '?' + qs : ''}`);
  },
  getTransactionSummary: () => api.get('/api/v1/admin/payment/transactions/summary'),
};

// SMS Gateway Admin API
export const smsGatewayAPI = {
  // List all SMS settings
  getSettings: () => api.get('/api/v1/admin/sms/settings'),

  // Update an SMS setting
  updateSetting: (key, value, description) =>
    api.put('/api/v1/admin/sms/settings', { key, value, ...(description ? { description } : {}) }),

  // Enable / disable SMS service globally
  toggle: (enabled) => api.post('/api/v1/admin/sms/toggle', { enabled }),

  // Switch active SMS provider
  switchProvider: (provider) =>
    api.post('/api/v1/admin/sms/switch-provider', { provider }),

  // Send a test SMS
  testSMS: (mobile, message) =>
    api.post('/api/v1/admin/sms/test', { mobile, message }),
};

// Health Check APIs (Optional - for system monitoring)
export const healthAPI = {
  check: () => api.get('/api/v1/health'),
  test: () => api.get('/api/v1/test'),
};

// DEPRECATED APIs - Kept for backward compatibility, will be removed in future versions
// Use getAllForUser() instead of getByFarm() for ponds
// Use direct policy/claim endpoints instead of these helper methods

export default api;
